import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Give it a moment to load user, then stop checking
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 1000); // Max 1 second wait

    // If user is loaded, stop checking immediately
    if (user !== null || !isAuthenticated) {
      setIsChecking(false);
    }

    return () => clearTimeout(timer);
  }, [user, isAuthenticated]);

  console.log('AdminProtectedRoute - isAuthenticated:', isAuthenticated);
  console.log('AdminProtectedRoute - user:', user);
  console.log('AdminProtectedRoute - loading:', loading);
  console.log('AdminProtectedRoute - isChecking:', isChecking);
  console.log('AdminProtectedRoute - user roles:', user?.roles);

  // If not authenticated, redirect to login immediately
  if (!isAuthenticated) {
    console.log('AdminProtectedRoute - Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // If still checking or loading (but only briefly), show loading screen
  if ((loading || isChecking) && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 dark:border-gray-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yetki kontrol ediliyor...</p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {loading ? 'Kullanıcı bilgisi yükleniyor...' : 'Kontrol ediliyor...'}
          </p>
        </div>
      </div>
    );
  }

  // If authenticated but no user data after timeout, something went wrong
  if (!user) {
    console.error('AdminProtectedRoute - User is null after loading completed');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-2">Kullanıcı Bilgisi Yüklenemedi</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Kullanıcı bilgileriniz yüklenemedi. Lütfen tekrar giriş yapın.
          </p>
          <button
            onClick={() => {
              localStorage.removeItem('access_token');
              window.location.href = '/login';
            }}
            className="px-6 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            Giriş Sayfasına Dön
          </button>
        </div>
      </div>
    );
  }

  // Check if user has admin role
  const isAdmin = user?.roles?.includes('admin') || false;
  console.log('AdminProtectedRoute - isAdmin:', isAdmin);

  if (!isAdmin) {
    console.log('AdminProtectedRoute - Not admin, showing error');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-2">Yetkisiz Erişim</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Bu sayfaya erişim yetkiniz yok. Admin yetkisi gereklidir.
          </p>
          <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded mb-4 text-left transition-colors">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Kullanıcı:</strong> {user?.username}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Roller:</strong> {user?.roles?.join(', ') || 'Yok'}
            </p>
          </div>
          <button
            onClick={() => (window.location.href = '/dashboard')}
            className="px-6 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
          >
            Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }

  // User is admin, render children
  console.log('AdminProtectedRoute - Access granted');
  return <>{children}</>;
};

export default AdminProtectedRoute;

